/**
 * CAPTION IT — Cart Drawer custom
 *
 * Intercetta:
 *  • Click su [data-ci-cart-trigger] (icona carrello in nav, sticky bar) → apre drawer
 *  • Submit di form[action$="/cart/add"] → POST asincrono, refresh items, apre drawer
 *  • Click overlay / X / Escape → chiude
 *  • Quantity buttons / Remove dentro al drawer → POST /cart/change.js → re-render
 *
 * State: legge sempre da Shopify /cart.js (single source of truth, evita drift).
 */
(function () {
  'use strict';

  const SEL = {
    drawer:    '[data-ci-cart-drawer]',
    overlay:   '[data-ci-cart-overlay]',
    body:      '[data-ci-cart-body]',
    foot:      '[data-ci-cart-foot]',
    total:     '[data-ci-cart-total]',
    drawerCount: '[data-ci-cart-drawer-count]',
    closeBtn:  '[data-ci-cart-close]',
    trigger:   '[data-ci-cart-trigger]',
    /* Cart count nelle nav (esistente) — aggiorniamo in sync */
    navCount:  '#cart-count',
  };

  function $(sel, root)  { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function formatMoney(cents) {
    /* Shopify Liquid money_format è server-side; client-side usiamo
       semplice formatter EUR (default per IT). Customizzabile. */
    const euro = (cents / 100).toLocaleString('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return euro;
  }

  function trapFocus(container, e) {
    const focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last  = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  class CartDrawer {
    constructor(root) {
      this.root = root;
      this.body  = $(SEL.body, root);
      this.foot  = $(SEL.foot, root);
      this.totalEl = $(SEL.total, root);
      this.countEl = $(SEL.drawerCount, root);
      this.cart = null;

      this.bindGlobals();
      /* Iniziale: fetch cart se cart count > 0 */
      this.refresh().catch(() => {});
    }

    bindGlobals() {
      /* Open via trigger */
      document.addEventListener('click', e => {
        const trigger = e.target.closest(SEL.trigger);
        if (trigger) {
          e.preventDefault();
          this.open();
        }
        const closeBtn = e.target.closest(SEL.closeBtn);
        if (closeBtn) {
          e.preventDefault();
          this.close();
        }
        const overlay = e.target.closest(SEL.overlay);
        if (overlay) {
          this.close();
        }
      });

      /* Escape per chiudere */
      document.addEventListener('keydown', e => {
        if (this.root.getAttribute('aria-hidden') === 'true') return;
        if (e.key === 'Escape') { this.close(); return; }
        if (e.key === 'Tab') trapFocus(this.root, e);
      });

      /* Intercept form submit /cart/add */
      document.addEventListener('submit', e => {
        const form = e.target.closest('form[action$="/cart/add"]');
        if (!form) return;
        /* Skip se il form vuole proprio il submit standard */
        if (form.dataset.ciCartSkip === 'true') return;

        e.preventDefault();
        this.handleAdd(form);
      }, true);
    }

    async handleAdd(form) {
      const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
      const originalLabel = submitBtn?.textContent || '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'aggiungo…';
      }

      try {
        const formData = new FormData(form);
        /* Tentativo 1: API JSON /cart/add.js (preferito → tiene drawer aperto) */
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' },
        });
        if (r.ok) {
          await this.refresh();
          this.open();
          return;
        }

        /* Fallback: cart permalink GET (bypassa la 422 "is already sold out"
           di /cart/add.js, usa il route handler GET che spesso accetta) */
        const variantId = formData.get('id');
        if (!variantId) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.description || err.message || ('HTTP ' + r.status));
        }
        const params = new URLSearchParams();
        for (const [k, v] of formData.entries()) {
          if (k === 'id' || k === 'quantity' || k === 'form_type' || k === 'utf8') continue;
          /* properties[X] → attributes[X] (cart permalink usa attributes) */
          const cleaned = k.replace(/^properties\[/, 'attributes[');
          if (v) params.append(cleaned, v);
        }
        const qty = formData.get('quantity') || '1';
        window.location.href = '/cart/' + variantId + ':' + qty + '?' + params.toString();
        /* Non riabilitiamo il bottone — il browser sta navigando via */
      } catch (e) {
        alert('Errore: ' + (e.message || 'impossibile aggiungere al carrello'));
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalLabel;
        }
      }
    }

    async refresh() {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      this.cart = await r.json();
      this.render();
      this.updateNavCount();
    }

    updateNavCount() {
      const count = (this.cart?.item_count) || 0;
      const el = $(SEL.navCount);
      if (el) el.textContent = String(count);
      if (this.countEl) this.countEl.textContent = String(count);
    }

    render() {
      if (!this.body) return;
      const items = this.cart?.items || [];

      if (items.length === 0) {
        this.body.innerHTML = `
          <div class="ci-drawer__empty">
            <div class="ci-drawer__empty-emoji">🛒</div>
            <div class="ci-drawer__empty-title">il carrello è vuoto</div>
            <div class="ci-drawer__empty-sub">aggiungi un mazzo personalizzato per iniziare</div>
            <a href="/products" class="ci-btn ci-btn--outline-ink" style="margin-top: 18px;">vai al prodotto</a>
          </div>`;
        if (this.foot) this.foot.hidden = true;
        return;
      }

      this.body.innerHTML = '';
      items.forEach((item, idx) => {
        const row = this.makeItemRow(item, idx);
        this.body.appendChild(row);
      });

      if (this.foot) this.foot.hidden = false;
      if (this.totalEl) this.totalEl.textContent = formatMoney(this.cart.total_price);
    }

    makeItemRow(item, idx) {
      const row = document.createElement('div');
      row.className = 'ci-drawer__item';
      row.dataset.ciCartLineKey = item.key;

      /* Estrai line item properties visibili (escludi quelle _-prefixed) */
      const props = (item.properties || {});
      const visibleProps = Object.keys(props)
        .filter(k => k && !k.startsWith('_') && props[k])
        .map(k => `${k}: ${props[k]}`);

      /* Shopify CDN: usa il param ?width=N (sintassi nuova) invece del
         legacy _NxM filename pattern, che spesso ritorna 404. */
      const imgSrc = item.image
        ? item.image + (item.image.includes('?') ? '&' : '?') + 'width=160'
        : '';

      /* IMPORTANTE: ogni .ci-drawer__item-img contiene un <img> reale
         (non background-image) per evitare la regola di Dawn
         div:empty { display: none } che nasconderebbe il wrapper. */
      row.innerHTML = `
        <div class="ci-drawer__item-img">${imgSrc ? `<img src="${imgSrc}" alt="" loading="lazy">` : '<span class="ci-drawer__item-img-ph"></span>'}</div>
        <div class="ci-drawer__item-info">
          <div class="ci-drawer__item-title"></div>
          ${item.variant_title ? `<div class="ci-drawer__item-variant"></div>` : ''}
          ${visibleProps.length ? `<div class="ci-drawer__item-props"></div>` : ''}
          <div class="ci-drawer__item-bottom">
            <div class="ci-drawer__qty" role="group" aria-label="Quantità">
              <button type="button" class="ci-drawer__qty-btn" data-ci-cart-qty="dec" aria-label="Diminuisci">−</button>
              <span class="ci-drawer__qty-num"></span>
              <button type="button" class="ci-drawer__qty-btn" data-ci-cart-qty="inc" aria-label="Aumenta">+</button>
            </div>
            <div class="ci-drawer__item-price"></div>
          </div>
        </div>
        <button type="button" class="ci-drawer__item-remove" data-ci-cart-remove aria-label="Rimuovi articolo">×</button>`;

      const imgEl = row.querySelector('.ci-drawer__item-img');
      if (!imgSrc) {
        imgEl.classList.add('ci-drawer__item-img--placeholder');
      }
      row.querySelector('.ci-drawer__item-title').textContent = item.product_title || '';
      const variantEl = row.querySelector('.ci-drawer__item-variant');
      if (variantEl) variantEl.textContent = item.variant_title;
      const propsEl = row.querySelector('.ci-drawer__item-props');
      if (propsEl) propsEl.textContent = visibleProps.join(' · ');
      row.querySelector('.ci-drawer__qty-num').textContent = String(item.quantity);
      row.querySelector('.ci-drawer__item-price').textContent = formatMoney(item.line_price);

      row.querySelector('[data-ci-cart-qty="dec"]').addEventListener('click', () => this.changeQty(idx + 1, item.quantity - 1));
      row.querySelector('[data-ci-cart-qty="inc"]').addEventListener('click', () => this.changeQty(idx + 1, item.quantity + 1));
      row.querySelector('[data-ci-cart-remove]').addEventListener('click', () => this.changeQty(idx + 1, 0));

      return row;
    }

    async changeQty(line, qty) {
      try {
        const r = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line, quantity: Math.max(0, qty) }),
        });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        this.cart = await r.json();
        this.render();
        this.updateNavCount();
      } catch (e) {
        alert('Errore aggiornamento carrello: ' + (e.message || 'riprova'));
      }
    }

    open() {
      this.root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ci-no-scroll');
      const panel = $('.ci-drawer__panel', this.root);
      panel?.focus();
      /* Refresh state on open per essere sicuri */
      this.refresh().catch(() => {});
    }

    close() {
      this.root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ci-no-scroll');
    }
  }

  function init() {
    const root = $(SEL.drawer);
    if (!root) return;
    new CartDrawer(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
